import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types/shelter';
import { mockUsers } from '../data/mockData';
import { supabase } from '@/integrations/supabase/client';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { ROLES, AppRole, EffectiveRole, getEffectiveRole } from '@/constants/roles';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string; needsPasswordReset?: boolean; mfaRequired?: boolean }>;
  signUp: (email: string, password: string, fullName: string, username: string, organizationName?: string) => Promise<{ success: boolean; error?: string; autoConfirmed?: boolean }>;
  createStaffUser: (email: string, password: string, fullName: string, username: string, role: AppRole) => Promise<{ success: boolean; error?: string; userId?: string }>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  /** @deprecated Always false — demo mode has been removed. Kept for backward compatibility. */
  isDemoMode: false;
  userRole: AppRole | null;
  effectiveRole: EffectiveRole | null;
  isTestUser: boolean;
  userSites: string[];
  organizationId: string | null;
  needsPasswordReset: boolean;
  setNeedsPasswordReset: (needs: boolean) => void;
  mfaPending: boolean;
  completeMFA: () => void;
  isLoading: boolean;
  profileLoadError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Test user emails for secure test environment
const TEST_USER_EMAILS = [
  'superadmin@demo.local',
  'manager1@demo.local',
  'staff1@demo.local'
];

const AUTH_BOOTSTRAP_TIMEOUT_MS = 10000;
const PROFILE_LOAD_TIMEOUT_MS = 12000;

const withTimeout = async <T,>(promise: PromiseLike<T>, ms: number, label: string): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [effectiveRole, setEffectiveRole] = useState<EffectiveRole | null>(null);
  const [isTestUser, setIsTestUser] = useState(false);
  const [userSites, setUserSites] = useState<string[]>([]);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [mfaPending, setMfaPending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profileLoadError, setProfileLoadError] = useState<string | null>(null);

  const clearUserState = () => {
    setUser(null);
    setUserRole(null);
    setEffectiveRole(null);
    setIsTestUser(false);
    setUserSites([]);
    setOrganizationId(null);
    setProfileLoadError(null);
  };

  useEffect(() => {
    let bootstrapped = false;
    let bootstrapTimer: ReturnType<typeof setTimeout> | undefined;

    const finishLoading = () => {
      if (!bootstrapped) {
        bootstrapped = true;
        if (bootstrapTimer) clearTimeout(bootstrapTimer);
      }
      setIsLoading(false);
    };

    bootstrapTimer = setTimeout(finishLoading, AUTH_BOOTSTRAP_TIMEOUT_MS);

    // Set up Supabase auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        if (session?.user) {
          setIsLoading(true);
          const needsReset = session.user.user_metadata?.needs_password_reset === true;
          setNeedsPasswordReset(needsReset);

          setTimeout(() => {
            loadUserProfile(session.user).finally(finishLoading);
          }, 0);
        } else {
          clearUserState();
          setNeedsPasswordReset(false);
          finishLoading();
        }
      }
    );

    // Check for existing Supabase session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        const needsReset = session.user.user_metadata?.needs_password_reset === true;
        setNeedsPasswordReset(needsReset);
        loadUserProfile(session.user).finally(finishLoading);
      } else {
        finishLoading();
      }
    }).catch(finishLoading);

    return () => {
      if (bootstrapTimer) clearTimeout(bootstrapTimer);
      subscription.unsubscribe();
    };
  }, []);

  const loadUserProfile = async (supabaseUser: SupabaseUser): Promise<boolean> => {
    try {
      setProfileLoadError(null);
      // Fetch profile; if missing (common when users are created via admin flows), create it on first login
      const {
        data: existingProfile,
        error: profileSelectError,
      } = await withTimeout(supabase
        .from('profiles')
        .select('*')
        .eq('id', supabaseUser.id)
        .maybeSingle(), PROFILE_LOAD_TIMEOUT_MS, 'Profile lookup');

      if (profileSelectError) throw profileSelectError;

      let profile = existingProfile;

      if (!profile) {
        const defaultFullName =
          (supabaseUser.user_metadata as any)?.full_name || supabaseUser.email || 'User';

        // Use upsert to handle race conditions where profile may already exist
        const { data: createdProfile, error: profileUpsertError } = await supabase
          .from('profiles')
          .upsert(
            {
              id: supabaseUser.id,
              full_name: defaultFullName,
            },
            { onConflict: 'id', ignoreDuplicates: true }
          )
          .select('*')
          .single();

        if (profileUpsertError) {
          // If upsert fails, try fetching again (profile might exist with different RLS context)
          const { data: refetchedProfile } = await withTimeout(supabase
            .from('profiles')
            .select('*')
            .eq('id', supabaseUser.id)
            .single(), PROFILE_LOAD_TIMEOUT_MS, 'Profile retry');
          
          profile = refetchedProfile;
        } else {
          profile = createdProfile;
        }
      }

      let { data: userRoleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', supabaseUser.id)
        .maybeSingle();

      if (roleError) throw roleError;

      // If no role exists, create a default 'staff' role
      if (!userRoleData) {
        const { data: newRole, error: roleInsertError } = await supabase
          .from('user_roles')
          .insert([
            {
              user_id: supabaseUser.id,
              role: 'staff',
            },
          ])
          .select()
          .single();

        if (roleInsertError) throw roleInsertError;
        userRoleData = newRole;
      }

      // Load user's assigned sites
      const { data: assignedSites, error: sitesError } = await supabase
        .from('user_sites')
        .select('site_id')
        .eq('user_id', supabaseUser.id);

      if (sitesError) throw sitesError;

      // Load user's organization
      const { data: orgMembership } = await withTimeout(supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', supabaseUser.id)
        .maybeSingle(), PROFILE_LOAD_TIMEOUT_MS, 'Organization lookup');

      // Detect test users by email (removed insecure localStorage override)
      const isTest = TEST_USER_EMAILS.includes(supabaseUser.email || '');

      if (profile && userRoleData) {
        const actualRole = userRoleData.role as AppRole;
        const userData: User = {
          id: profile.id,
          username: profile.full_name || supabaseUser.email || '',
          name: profile.full_name || supabaseUser.email || '',
          role: actualRole as
            | 'staff'
            | 'supervisor'
            | 'manager'
            | 'super_admin'
            | 'program_director'
            | 'case_manager'
            | 'residential_aide',
        };
        setUser(userData);
        setUserRole(actualRole);
        setEffectiveRole(getEffectiveRole(actualRole));
        setIsTestUser(isTest);
        setUserSites(assignedSites?.map((s) => s.site_id) || []);
        setOrganizationId(orgMembership?.organization_id || null);
        return true;
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      clearUserState();
      setProfileLoadError(error instanceof Error ? error.message : 'Unable to load account access');
    }

    return false;
  };

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string; needsPasswordReset?: boolean; mfaRequired?: boolean }> => {
    // Try Supabase authentication with email
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: username.trim(),
        password: password,
      });

      if (error) {
        // Provide helpful error message if username was used instead of email
        if (!username.includes('@')) {
          return { success: false, error: 'Please use your email address to log in (not username)' };
        }
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Check MFA status
        const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        
        if (!aalError && aalData.currentLevel === 'aal1' && aalData.nextLevel === 'aal2') {
          // User has 2FA enrolled but hasn't verified yet this session
          const profileLoaded = await loadUserProfile(data.user);
          if (!profileLoaded) {
            return { success: false, error: 'Signed in, but account access did not load. Refresh and try again.' };
          }
          setMfaPending(true);
          return { success: true, mfaRequired: true };
        }

        const profileLoaded = await loadUserProfile(data.user);
        if (!profileLoaded) {
          return { success: false, error: 'Signed in, but account access did not load. Refresh and try again.' };
        }
        
        // Log the login activity
        await supabase.from('user_activity_logs').insert({
          user_id: data.user.id,
          action_type: 'login',
          user_agent: navigator.userAgent
        });
        
        // Update last login timestamp
        await supabase.from('profiles').update({
          last_login: new Date().toISOString()
        }).eq('id', data.user.id);
        
        // Check if user needs password reset
        const needsReset = data.user.user_metadata?.needs_password_reset === true;
        return { success: true, needsPasswordReset: needsReset };
      }

      return { success: false, error: 'Login failed' };
    } catch (error: any) {
      return { success: false, error: error?.message || 'An error occurred during login' };
    }
  };

  const signUp = async (email: string, password: string, fullName: string, username: string, organizationName?: string): Promise<{ success: boolean; error?: string; autoConfirmed?: boolean }> => {
    try {
      const trimmedOrg = (organizationName ?? '').trim();
      const metadata: Record<string, any> = {
        full_name: fullName,
        username: username,
      };
      // Pass the org name through verbatim if provided. Blank → omit so the DB
      // trigger can decide (first super_admin still gets bootstrapped; everyone
      // else lands on the invite path).
      if (trimmedOrg) metadata.organization_name = trimmedOrg;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: metadata,
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.session) {
        await loadUserProfile(data.user!);
        return { success: true, autoConfirmed: true };
      }

      return { success: true, autoConfirmed: false };
    } catch (error) {
      return { success: false, error: 'An error occurred during sign up' };
    }
  };


  const createStaffUser = async (email: string, password: string, fullName: string, username: string, role: 'staff' | 'supervisor' | 'manager'): Promise<{ success: boolean; error?: string; userId?: string }> => {
    try {
      // Create the user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
            username: username,
            needs_password_reset: true
          }
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Assign role to the new user
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert([{
            user_id: data.user.id,
            role: role
          }]);

        if (roleError) {
          return { success: false, error: `User created but role assignment failed: ${roleError.message}` };
        }

        return { success: true, userId: data.user.id };
      }

      return { success: false, error: 'User creation failed' };
    } catch (error: any) {
      return { success: false, error: error.message || 'An error occurred during user creation' };
    }
  };

  const completeMFA = () => {
    setMfaPending(false);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    clearUserState();
    setMfaPending(false);
  };

  const value = {
    user,
    login,
    signUp,
    createStaffUser,
    logout,
    isAuthenticated: !!session,
    isDemoMode: false as const,
    userRole,
    effectiveRole,
    isTestUser,
    userSites,
    organizationId,
    needsPasswordReset,
    setNeedsPasswordReset,
    mfaPending,
    completeMFA,
    isLoading,
    profileLoadError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Re-export for convenience
export { AuthContext };